import React, { useEffect } from 'react';
import styled from 'styled-components';

export const PLACEHOLDER = 'placeholder';

const PlaceholderComponent = ({ attributes, children }) => {
  const handleMouseDown = event => {
    // Prevent scroll-to-top behavior when clicking near placeholder
    const scrollContainer = event.target.closest('[data-aid="slate-editor-container"]');
    if (scrollContainer) {
      // Find scrollable element using a more flexible approach
      const scrollableElement = Array.from(scrollContainer.querySelectorAll('div')).find(
        el => el.style.overflowY === 'auto'
      );
      if (scrollableElement) {
        const originalScrollTop = scrollableElement.scrollTop;

        // Use requestAnimationFrame to restore scroll position after DOM changes
        requestAnimationFrame(() => {
          if (scrollableElement.scrollTop !== originalScrollTop) {
            scrollableElement.scrollTop = originalScrollTop;
          }
        });
      }
    }
  };

  return (
    <StyledPlaceholder {...attributes} onMouseDown={handleMouseDown}>
      {children}
    </StyledPlaceholder>
  );
};

export const StyledPlaceholder = styled.span.attrs({
  className: 'slate-placeholder',
})`
  border: 1px solid var(--color-gray500);
  border-radius: 4px;
  color: var(--color-gray500);
  padding: 2px 4px;
  span::selection {
    background-color: var(--color-selection-background);
    color: var(--color-selection-text);
  }
`;

export default function PlaceholdersPlugin(options) {
  return {
    onChange(editor, next) {
      return editor.ensureFullSelection(next, PLACEHOLDER);
    },

    onKeyDown(evt, editor, next) {
      const { value } = editor;
      const { document, selection } = value;

      const currentPlaceholder = editor.value.inlines.find(i => i.type === PLACEHOLDER);
      if (evt.key !== 'Tab') {
        return next();
      }

      // If we have a placeholder selected, we need to ensure we find the _next_ one, so move the start selection
      // up past it.
      if (currentPlaceholder) {
        const nextNode = editor.value.document.getNextNode(currentPlaceholder.key);
        // This means the placeholder is actually the last node - in which case Tab should proceed as normal.
        if (!nextNode) {
          return next();
        }
        // I use Anchor instead of Start because I don't think Slate likes it when I move the start past the end..
        editor.moveAnchorToStartOfNode(nextNode);
      }

      // Select the rest of the document so that we can find the next placeholder within it.
      // For some reason editor.moveEndToEndOfDocument() doesn't work..
      editor.moveFocusToEndOfNode(document.getTexts().last());
      const nextPlaceholder = document.getLeafInlinesAtRange(editor.value.selection).find(i => i.type === PLACEHOLDER);
      if (!nextPlaceholder) {
        editor.select(selection); // Move the selection back into place
        return next();
      }

      evt.preventDefault();
      editor.moveEndToEndOfNode(nextPlaceholder);
      editor.moveStartToStartOfNode(nextPlaceholder);
      return true;
    },

    renderNode(props, editor, next) {
      if (props.node.type === PLACEHOLDER) {
        const { attributes, children } = props;
        return <PlaceholderComponent {...attributes}>{children}</PlaceholderComponent>;
      }
      return next();
    },

    schema: {
      inlines: {
        [PLACEHOLDER]: {
          nodes: [
            {
              match: { object: 'text' },
              min: 1,
              max: 1,
            },
          ],
          text: /.+/, // Can't have empty placeholders
        },
      },
    },
  };
}

export function findFirstPlaceholderNode(editor) {
  const placeholders = editor.value.document.getInlinesByType(PLACEHOLDER);
  if (!placeholders.count()) {
    return null;
  }

  return placeholders.get(0);
}

export function useFocusFirstPlaceholder(editor, onChange) {
  useEffect(() => {
    if (editor) {
      const firstPlaceholderNode = findFirstPlaceholderNode(editor);
      if (!firstPlaceholderNode) {
        return;
      }
      editor.moveStartToStartOfNode(firstPlaceholderNode);
      editor.moveEndToEndOfNode(firstPlaceholderNode);
      onChange(editor);
    }
  }, [editor, onChange]);
}
